import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const resources = [
  { icon: 'ri-file-text-line', title: 'New Patient Forms', desc: 'Download and complete your intake forms before your first visit to save time.', link: '#' },
  { icon: 'ri-medicine-bottle-line', title: 'Prescription Refills', desc: 'Request a prescription refill through our patient portal or by calling our office.', link: '#' },
  { icon: 'ri-test-tube-line', title: 'Lab Results', desc: 'Access your lab results securely through our online patient portal 24/7.', link: '#' },
  { icon: 'ri-shield-cross-line', title: 'Insurance & Billing', desc: 'Understand your coverage, view statements, and pay bills online.', link: '#' },
  { icon: 'ri-computer-line', title: 'Patient Portal', desc: 'Message your care team, view visit summaries, and manage your health records.', link: '#' },
  { icon: 'ri-heart-pulse-line', title: 'Health Education', desc: 'Browse our library of articles on managing chronic conditions and staying healthy.', link: '#' },
];

const faqs = [
  { q: 'How do I become a new patient?', a: 'Simply book an appointment online or call our office. Bring a valid ID, insurance card, and any relevant medical records to your first visit.' },
  { q: 'Do you accept walk-ins?', a: 'We recommend scheduling in advance, but we do accommodate same-day urgent visits. Call us first to check availability.' },
  { q: 'How do I request a prescription refill?', a: 'Log into the patient portal, call our office, or ask your pharmacy to send a refill request directly to us.' },
  { q: 'Can I do a telehealth visit?', a: 'Yes! Telehealth is available for most non-emergency concerns. Book a telehealth visit through our appointment page.' },
  { q: 'What should I bring to my first appointment?', a: 'Please bring a photo ID, your insurance card, a list of current medications, and any prior medical records or test results.' },
  { q: 'How do I access my lab results?', a: 'Results are available through our secure patient portal, usually within 1–3 business days. You\'ll receive a notification when they\'re ready.' },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-white rounded-2xl border border-zinc-100 overflow-hidden cursor-pointer"
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between px-6 py-5">
        <span className="font-semibold text-zinc-900 text-sm pr-4">{q}</span>
        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-zinc-100 shrink-0">
          <i className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-rose-500 text-base`}></i>
        </div>
      </div>
      {open && (
        <div className="px-6 pb-5 text-zinc-500 text-sm leading-relaxed border-t border-zinc-50 pt-4">{a}</div>
      )}
    </div>
  );
}

export default function ResourcesPage() {
  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-rose-500 via-rose-400 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 to-zinc-950/95"></div>
        <div className="relative z-10 px-8 md:px-16 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-rose-500"></div>
            <span className="text-rose-400 text-xs font-semibold uppercase tracking-[0.25em]">Support & Tools</span>
          </div>
          <h1
            className="text-5xl md:text-7xl font-black text-white leading-tight mb-6"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Patient<br /><span className="text-rose-500">Resources</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl leading-relaxed">
            Everything you need to manage your health and stay connected with your care team.
          </p>
        </div>
      </section>

      {/* Resources grid */}
      <section className="bg-zinc-50 py-20">
        <div className="px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
            {resources.map((r, idx) => (
              <a
                key={r.title}
                href={r.link}
                className="group bg-white p-7 rounded-2xl border border-zinc-100 hover:border-rose-200 transition-all duration-300 cursor-pointer block"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-zinc-50 group-hover:bg-rose-50 transition-colors">
                    <i className={`${r.icon} text-xl text-zinc-400 group-hover:text-rose-500 transition-colors`}></i>
                  </div>
                  <span
                    className="text-zinc-200 font-black text-3xl group-hover:text-rose-100 transition-colors"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3
                  className="text-base font-black text-zinc-900 mb-2"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  {r.title}
                </h3>
                <p className="text-zinc-500 text-sm leading-relaxed mb-5">{r.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-rose-500 text-sm font-semibold group-hover:gap-2.5 transition-all">
                  Access <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            ))}
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-rose-500"></div>
              <span className="text-rose-500 text-xs font-semibold uppercase tracking-[0.25em]">FAQ</span>
            </div>
            <h2
              className="text-3xl md:text-5xl font-black text-zinc-900 mb-10"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              Frequently Asked<br />Questions
            </h2>
            <div className="max-w-3xl space-y-3">
              {faqs.map((faq) => (
                <FAQItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="bg-zinc-950 rounded-3xl p-12 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-rose-500"></div>
                <span className="text-rose-400 text-xs font-semibold uppercase tracking-[0.25em]">Still Have Questions?</span>
              </div>
              <h3
                className="text-3xl font-black text-white"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                We're Here to Help
              </h3>
              <p className="text-zinc-400 text-sm mt-2">Our team is ready to answer any questions you have.</p>
            </div>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-rose-500 text-white font-bold rounded-full hover:bg-rose-400 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm shrink-0"
            >
              Contact Us
              <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full group-hover:translate-x-1 transition-transform">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
